import { Link, useParams } from 'react-router-dom';
import EmptyState from '@/components/feature/EmptyState';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { formatDate, getMediaById, getNewsById, mediaPath, relatedNews } from '@/lib/catalog';

export default function NewsDetail() {
  const { id = '' } = useParams();
  const item = getNewsById(id);

  useDocumentTitle(
    item ? `${item.title} - 影视资讯 - 风驰影院` : '影视资讯 - 风驰影院',
    item ? item.summary : '风驰影院影视资讯详情'
  );

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1240px] px-4 py-16 md:px-6">
        <EmptyState icon="ri-newspaper-line" title="未找到该资讯" description="该资讯可能已删除或链接有误，去看看其他影视动态吧。" />
        <div className="mt-5 flex justify-center">
          <Link to="/category/news" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition hover:bg-primary-600">
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  const mentioned = item.relatedIds.map((mid) => getMediaById(mid)).filter(Boolean);
  const recommendations = relatedNews(item, 4);

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm" aria-label="面包屑">
        <Link to="/" className="transition hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/category/news" className="transition hover:text-primary-600">影视资讯</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="line-clamp-1 text-foreground-900">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_336px]">
        <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            {item.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-foreground-500 md:text-sm">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {formatDate(item.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              风驰影院编辑部
            </span>
          </div>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 影视资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <p className="mt-6 rounded-lg bg-background-100 p-4 text-sm leading-7 text-foreground-700">
            {item.summary}
          </p>

          <div className="mt-6 space-y-5">
            {item.content.map((paragraph, idx) => (
              <p key={idx} className="text-sm leading-8 text-foreground-700 md:text-[15px]">
                {paragraph}
              </p>
            ))}
          </div>

          {mentioned.length > 0 && (
            <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-film-line text-primary-500" />
                文中提及影片
              </h2>
              <div className="mt-4 flex flex-wrap gap-3">
                {mentioned.map((media) =>
                  media ? (
                    <Link
                      key={media.id}
                      to={mediaPath(media)}
                      className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2 pr-4 transition hover:border-primary-300"
                    >
                      <span className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                        <img src={media.poster} alt={media.title} className="h-full w-full object-cover object-top" />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900 transition group-hover:text-primary-600">
                          {media.title}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {media.kind === 'movie' ? '电影' : '电视剧'} · {media.year}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line ml-1 text-lg text-foreground-400" />
                    </Link>
                  ) : null
                )}
              </div>
            </section>
          )}
        </article>

        <aside>
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 pb-3 font-heading text-base font-extrabold text-foreground-950">
              <i className="ri-links-line text-primary-500" />
              相关推荐资讯
            </h2>
            <div className="space-y-4">
              {recommendations.map((rec) => (
                <Link
                  key={rec.id}
                  to={`/detail/news/${rec.id}`}
                  className="group flex gap-3 border-b border-background-200 pb-4 last:border-b-0 last:pb-0"
                >
                  <span className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={rec.cover}
                      alt={rec.title}
                      className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm font-medium text-foreground-900 transition group-hover:text-primary-600">
                      {rec.title}
                    </span>
                    <span className="mt-1 block text-xs text-foreground-400">{formatDate(rec.date)}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}